import {
  getGetModuliQueryOptions,
  getModuli,
} from '@framework/api-client/api/inquiries/moduli-controller/moduli-controller.ts';
import { type GetModuliParams } from '@framework/api-client/api/inquiries/schemas/getModuliParams.ts';
import { type ModuloDTO } from '@framework/api-client/api/inquiries/schemas/moduloDTO.ts';
import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { AssignableExpressionInput } from '@framework/engine/schema/expressions/repository/assignable/input/AssignableExpressionInput.tsx';
import { ConditionGroupInput } from '@framework/engine/schema/expressions/repository/condition-group/input/ConditionGroupInput.tsx';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { useTreeReferencesContext } from '@framework/engine/tree/references/TreeReferencesContext.tsx';
import { useField } from '@framework/form/field/useField.tsx';
import { AsyncSelectInput } from '@framework/form/inputs/AsyncSelectInput.tsx';
import { CheckboxInput } from '@framework/form/inputs/CheckboxInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { ColumnSortDirections } from '@framework/ui/components/table/TableColumnSort.tsx';
import { getPagination } from '@framework/ui/core/pagination/getPagination.ts';
import { useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { type IModuleTriggerWidget } from './IModuleTriggerWidget.ts';

const InputBorder = styled.div`
  border: 1px solid ${props => props.theme.colorBorder};
  border-radius: 6px;
  padding: 8px;
`;

export interface IModuleTriggerConfigComponentProps
  extends IWidgetComponentProps<IModuleTriggerWidget> {
  /**
   * Permette di aggiungere configurazioni extra al componente
   */
  extraConfiguration?: React.ReactNode;
  /**
   * Permette di aggiungere parametri extra alla chiamata per ottenere i moduli
   */
  extraGetModuleParams?: Partial<GetModuliParams>;
  /**
   * Chiave extra che concorre al rimonto della select e al reset di
   * "moduleId": al suo cambio il modulo selezionato non è più valido
   * anche se i parametri della query non sono cambiati
   */
  extraModuleSelectKey?: string;
  moduleSelectDisabled?: boolean;

  hideModuleSelect?: boolean;
  hideTitleInput?: boolean;
  hideUniqueModuleInput?: boolean;
}

export function ModuleTriggerConfigComponent(
  props: IModuleTriggerConfigComponentProps,
) {
  const { hideModuleSelect, hideTitleInput, hideUniqueModuleInput } = props;

  const needUniqueModuleCode = useWatch({
    name: 'needUniqueModuleCode',
    exact: true,
  });

  const { references } = useTreeReferencesContext();

  const expressionReferences = useMemo(() => {
    if (!needUniqueModuleCode) return [];
    return references.filter(r => r.field.type === Datatype.String);
  }, [needUniqueModuleCode, references]);

  const initialParams = useMemo(
    () => ({
      ...getPagination({
        page: 0,
        pageSize: 20,
        direction: ColumnSortDirections.ascend,
        sort: 'titolo',
      }),
      ...props.extraGetModuleParams,
    }),
    [props.extraGetModuleParams],
  );

  // Rimonta la select al cambio del filtro extra: senza rimonto
  // `keepPreviousData` lascerebbe selezionabili le opzioni del filtro
  // precedente durante il caricamento delle nuove
  const moduleSelectKey = JSON.stringify([
    props.extraGetModuleParams ?? {},
    props.extraModuleSelectKey,
  ]);

  const moduleIdField = useField<string | null>('moduleId');
  const moduleSelectKeyRef = useRef(moduleSelectKey);

  // Effect per resettare il campo "moduleId": cambiando il filtro extra
  // cambiano i moduli selezionabili, ma la select asincrona non scarta i
  // valori assenti dalle nuove opzioni. Senza il reset resterebbe salvabile
  // un modulo appartenente al filtro precedente.
  useEffect(() => {
    if (moduleSelectKeyRef.current !== moduleSelectKey) {
      moduleSelectKeyRef.current = moduleSelectKey;
      moduleIdField.setValue(null);
    }
  }, [moduleSelectKey, moduleIdField]);

  return (
    <>
      <FormItem name="code" label="Codice">
        <TextInput name="code" />
      </FormItem>

      <div style={{ marginBottom: '16px' }}>{props.extraConfiguration}</div>

      {!hideModuleSelect && (
        <FormItem name="moduleId" label="Modulo">
          <AsyncSelectInput
            key={moduleSelectKey}
            disabled={props.moduleSelectDisabled}
            skip={() => !!props.moduleSelectDisabled}
            style={{ width: 'calc(100% - 100px)' }}
            placeholder="Cerca e aggiungi Modulo..."
            queryFn={getModuli}
            queryOptions={getGetModuliQueryOptions}
            initialParams={initialParams}
            searchByField={'titolo'}
            asOption={(module: ModuloDTO) => ({
              value: module.id ?? '<unknown>',
              label: module.titolo ?? 'Modulo senza nome',
              disabled: !module.id,
            })}
            name="moduleId"
            responseMeta={response => ({
              shown: response.dati?.length ?? 0,
              total: response.meta?.totale ?? 0,
            })}
          />
        </FormItem>
      )}

      <FormItem name="expression" label="Espressione">
        <InputBorder>
          <ConditionGroupInput name={`expression`} references={references} />
        </InputBorder>
      </FormItem>

      {!hideTitleInput && (
        <FormItem
          name="title"
          label="Titolo del modulo"
          help="Permette di fornire un titolo identificativo aggiuntivo al modulo per permettere all'utente di distinguere moduli duplicati"
        >
          <AssignableExpressionInput
            field={{ type: Datatype.String }}
            references={references}
            name={'title'}
            block
            size="large"
          />
        </FormItem>
      )}

      {!hideUniqueModuleInput && (
        <FormItem
          name="needUniqueModuleCode"
          label={null}
          layout={{ removeLabelSpace: true }}
          help="Impostando un codice, tutti i widget con lo stesso codice attiveranno il modulo un'unica volta."
        >
          <CheckboxInput name="needUniqueModuleCode" defaultChecked={false}>
            Imposta codice univoco di attivazione
          </CheckboxInput>
        </FormItem>
      )}

      {needUniqueModuleCode && !hideUniqueModuleInput && (
        <FormItem name="uniqueModuleCode" label="Codice univoco di attivazione">
          <AssignableExpressionInput
            field={{ type: Datatype.String }}
            references={expressionReferences}
            name={'uniqueModuleCode'}
            block
            size="large"
          />
        </FormItem>
      )}
    </>
  );
}
